import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Badge, BmModal, Btn, ConfirmModal, EmptyState, ErrorState, Loading, Overline, SearchBox } from '../../ForBrands/ui/BrandUI';
import { API_BASE_URL } from '../../../constants';

// Admin: the athlete's linked Cerebro records (users/cerebro_records.py).
// Cerebro sometimes files one player under several records (JJ's 2025
// summer sits on "Jahriq Sati-Grier"); a linked record's games count as the
// athlete's, on the profile and in scoring. "Suggested records" lists the
// ones that probably are this player, for every athlete; the admin checks
// each and links it with the same Link and confirm as a search result.
// An athlete with no main record (a SportsRadar team's athlete) links
// records too: those are who the athlete is in Cerebro, and their stats
// stand in when the team's feed has none for the athlete.
//
// Notes 10/19: the search also takes a pasted Cerebro player id; Link sits on
// every record an admin may link, its confirm showing the name score and any
// holder (a pilot profile's hold is released, a live athlete's record is
// refused); a name that does not match is linked as an override (the admin
// vouches for it); the refresh after a change says honestly what happened and
// what each record added; a record the AUTO tier linked reads "Linked
// automatically". Staff only, so the wording names Cerebro.

const recordsUrl = (email) => `${API_BASE_URL}/admin/athletes/${encodeURIComponent(email)}/cerebro-records/`;

// fetch, as the Users page calls the API. A refusal throws an error that
// carries {response: {status, data}}, the server's {error, message}.
const request = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(url, {
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  let data = {};
  try {
    data = (await res.json()) || {};
  } catch (e) {
    data = {};
  }
  if (!res.ok) {
    const err = new Error(data.message || `Request failed (${res.status})`);
    err.response = { status: res.status, data };
    throw err;
  }
  return data;
};

// {email, name, mainId, main, linked, maxLinked, cerebroError}
export const fetchCerebroRecords = (email) => request(recordsUrl(email));

// {name, candidates: [{id, name, team, competition, years, games, isMain, isLinked, linkedTo, nameScore, nameOk}]}
// `name` may be a pasted Cerebro player id.
export const searchCerebroRecords = (email, name) => request(`${recordsUrl(email)}search/?name=${encodeURIComponent(name)}`);

// The records that are probably the same player (cerebro_records.suggest_records),
// worked out when asked and never linked on their own: {email, name, mainId,
// suggestions: [{id, name, team, competition, years, games, confidence, reasons, camp}]}
export const fetchCerebroSuggestions = (email) => request(`${recordsUrl(email)}suggestions/`);

// {add: [id]}, {add: [id], override: true}, {remove: [id]} or {refresh: true};
// answers the records plus {status, added, removed, skipped, released, refresh}.
export const changeCerebroRecords = (email, payload) => request(recordsUrl(email), {
  method: 'POST', body: JSON.stringify(payload),
});

// A Cerebro player id (a uuid), as an admin pastes it.
const ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isRecordId = (text) => ID_RE.test(String(text || '').trim());

// The confirm's lines about a record before it is linked: its name score, a
// name that does not match (linked as an override), a pilot profile holding it,
// or another profile whose stats a name search found on it (byName).
export const linkNotes = (record = {}, athleteLabel = 'this athlete') => {
  const notes = [];
  if (typeof record.nameScore === 'number') notes.push(`Name match: ${record.nameScore} of 100.`);
  if (record.nameOk === false) {
    notes.push(`The name on this record does not match ${athleteLabel}. Linking it is an admin override: you vouch that it is this athlete.`);
  }
  const holder = record.linkedTo || record.heldBy;
  if (holder && holder.releasable && holder.byName) {
    notes.push(`The stats on ${holder.name || holder.email || 'another profile'} came from a name search on this record. Linking it here makes it this athlete's, and that profile drops it on its next refresh.`);
  } else if (holder && holder.releasable) {
    notes.push(`It is on the pilot profile of ${holder.name || holder.email || 'another record'}. Linking it here releases it from that profile.`);
  }
  return notes;
};

export const formatYears = (years) => {
  if (!Array.isArray(years) || years.length === 0) return '';
  return years.length === 1 ? String(years[0]) : `${years[0]} to ${years[years.length - 1]}`;
};

export const recordMeta = (record = {}) => [record.team, record.competition].filter(Boolean).join(' · ');

export const recordSpan = (record = {}) => {
  const games = Number(record.games) || 0;
  return [formatYears(record.years), games ? `${games} ${games === 1 ? 'game' : 'games'}` : ''].filter(Boolean).join(' · ');
};

const serverMessage = (err, fallback) => {
  const text = err?.response?.data?.message;
  return typeof text === 'string' && text.trim() ? text : fallback;
};

const REFRESH_TEXT = {
  done: 'Stats refreshed.',
  running: 'Stats are still refreshing and update in a minute.',
  failed: 'The stats refresh did not finish. Use Refresh stats to try again.',
  cerebro_unavailable: 'Cerebro did not answer, so the stats did not refresh. They refresh on their own once it is back.',
};

const LINK_SOURCE = {
  auto_link: { label: 'Linked automatically', tone: 'soft' },
  admin_override: { label: 'Override', tone: 'outline' },
  merge: { label: 'From a merge', tone: 'outline' },
};

const SEARCH_MIN = 2;

const CONFIDENCE = {
  high: { label: 'High confidence', tone: 'dark' },
  medium: { label: 'Medium confidence', tone: 'soft' },
};

const RecordLine = ({ record, testId, details, children }) => {
  const meta = recordMeta(record);
  const span = recordSpan(record);
  return (
    <li
      data-testid={testId}
      className="flex items-center justify-between gap-3 rounded-[8px] border border-bm-border bg-white px-4 py-3"
    >
      <span className="min-w-0">
        <span className="block truncate bm-label-l text-bm-ink">{record.name || record.id}</span>
        {meta && <span className="block truncate bm-body-s text-bm-muted">{meta}</span>}
        {span && <span className="block bm-body-s text-bm-muted">{span}</span>}
        {details}
        {record.found === false && <span className="block bm-body-s text-bm-critical">Not found in Cerebro</span>}
        {LINK_SOURCE[record.source] && (
          <span className="mt-1 flex"><Badge tone={LINK_SOURCE[record.source].tone}>{LINK_SOURCE[record.source].label}</Badge></span>
        )}
        {record.name && <span className="block truncate bm-label-xs text-bm-tertiary">{record.id}</span>}
      </span>
      {children && <span className="flex shrink-0 items-center gap-2">{children}</span>}
    </li>
  );
};

// A suggestion's confidence, a Camp badge for a camp or all-star record (its
// games show on the timeline and stay out of scoring), and why it was suggested.
const SuggestionDetails = ({ suggestion }) => {
  const confidence = CONFIDENCE[suggestion.confidence] || CONFIDENCE.medium;
  const reasons = (Array.isArray(suggestion.reasons) ? suggestion.reasons : []).filter(Boolean);
  return (
    <span className="mt-2 flex flex-col gap-1">
      <span className="flex flex-wrap items-center gap-2">
        <Badge tone={confidence.tone}>{confidence.label}</Badge>
        {suggestion.camp && <Badge tone="outline">{suggestion.camp}</Badge>}
      </span>
      {reasons.length > 0 && <span className="block bm-body-s text-bm-muted">{reasons.join(' · ')}</span>}
    </span>
  );
};

const LinkedCerebroRecordsDialog = ({ open, athlete, onClose, onChange }) => {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [name, setName] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [suggestions, setSuggestions] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const alive = useRef(true);
  // Suggestions can take a few seconds (several Cerebro searches): only the
  // newest request's answer is shown, never one asked for another athlete.
  const suggestRun = useRef(0);
  const email = athlete?.email || '';

  useEffect(() => () => { alive.current = false; }, []);

  const load = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    setLoadError(false);
    try {
      const data = await fetchCerebroRecords(email);
      if (alive.current) setView(data);
    } catch (err) {
      if (alive.current) setLoadError(true);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [email]);

  const loadSuggestions = useCallback(async () => {
    if (!email) return;
    suggestRun.current += 1;
    const run = suggestRun.current;
    const current = () => alive.current && run === suggestRun.current;
    setSuggesting(true);
    setSuggestError('');
    try {
      const data = await fetchCerebroSuggestions(email);
      if (current()) setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
    } catch (err) {
      if (current()) {
        setSuggestions(null);
        setSuggestError(serverMessage(err, 'Could not load the suggestions.'));
      }
    } finally {
      if (current()) setSuggesting(false);
    }
  }, [email]);

  useEffect(() => {
    if (!open || !email) return;
    setView(null);
    setResults(null);
    setSearchError('');
    setSuggestions(null);
    setSuggestError('');
    setConfirm(null);
    setOutcome(null);
    setName(athlete?.surname || '');
    load();
    loadSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, email]);

  if (!open || !athlete) return null;

  const athleteLabel = view?.name || `${athlete.name || ''} ${athlete.surname || ''}`.trim() || email;
  const linked = view?.linked || [];
  const maxLinked = view?.maxLinked || 8;
  const linkedIds = new Set(linked.map((r) => r.id));
  const full = linked.length >= maxLinked;
  const canSearch = name.trim().length >= SEARCH_MIN;

  const runSearch = async () => {
    if (!canSearch || searching) return;
    setSearching(true);
    setSearchError('');
    try {
      const data = await searchCerebroRecords(email, name.trim());
      if (alive.current) setResults(data.candidates || []);
    } catch (err) {
      if (alive.current) {
        setResults(null);
        setSearchError(serverMessage(err, 'The search did not go through. Try again.'));
      }
    } finally {
      if (alive.current) setSearching(false);
    }
  };

  const announce = (lead, data) => {
    const status = data?.refresh?.status;
    const text = [lead, REFRESH_TEXT[status]].filter(Boolean).join(' ');
    if (status === 'failed' || status === 'cerebro_unavailable') toast.warning(text);
    else toast.success(text);
  };

  // What each changed record did to the stats, as the refresh says it
  // ("Adds 0 games to 2025-26: its games are in 2024-25.").
  const keepOutcome = (data, record) => {
    const items = Array.isArray(data?.refresh?.contributions) ? data.refresh.contributions : [];
    if (!items.length) {
      setOutcome(null);
      return;
    }
    const names = { [record?.id]: record?.name };
    setOutcome(items.map((item) => ({ id: item.id, name: names[item.id] || item.id, text: item.text })));
  };

  const submit = async ({ kind, record }) => {
    setBusy(true);
    try {
      const payload = kind === 'add'
        ? { add: [record.id], ...(record.nameOk === false ? { override: true } : {}) }
        : { remove: [record.id] };
      const data = await changeCerebroRecords(email, payload);
      if (alive.current) {
        setView(data);
        keepOutcome(data, record);
      }
      if (data.status === 'unchanged') toast.info('Nothing changed.');
      else announce(kind === 'add' ? 'Record linked.' : 'Record removed.', data);
      onChange?.();
    } catch (err) {
      toast.error(serverMessage(err, 'That did not go through. Try again.'));
    } finally {
      if (alive.current) {
        setBusy(false);
        setConfirm(null);
      }
    }
  };

  const refreshOnly = async () => {
    setBusy(true);
    try {
      const data = await changeCerebroRecords(email, { refresh: true });
      if (alive.current) setView(data);
      const status = data?.refresh?.status;
      if (status === 'done') toast.success('Stats refreshed.');
      else if (status === 'running') toast.info(REFRESH_TEXT.running);
      else if (status === 'cerebro_unavailable') toast.warning(REFRESH_TEXT.cerebro_unavailable);
      else toast.warning('The stats refresh did not finish. Try again.');
      onChange?.();
    } catch (err) {
      toast.error(serverMessage(err, 'Could not refresh the stats. Try again.'));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const describe = (record) => [record.name || record.id, recordMeta(record)].filter(Boolean).join(', ');

  const candidateAction = (c) => {
    if (c.id === view?.mainId || (c.isMain && !view)) return <Badge tone="outline">Main record</Badge>;
    if (linkedIds.has(c.id)) return <Badge tone="outline" dot="#1f9d55">Linked</Badge>;
    const holder = c.linkedTo || c.heldBy;
    // A live athlete's record is theirs: the server refuses it, so no Link.
    if (holder && !holder.releasable) return <Badge tone="passive">On {holder.name || holder.email}</Badge>;
    return (
      <>
        {holder && <Badge tone="passive">On {holder.name || holder.email} ({holder.byName ? 'name search' : 'pilot'})</Badge>}
        <Btn
          size="sm"
          onClick={() => setConfirm({ kind: 'add', record: c })}
          disabled={busy || full || !view}
          title={full ? `An athlete can have at most ${maxLinked} linked records.` : undefined}
        >
          Link
        </Btn>
      </>
    );
  };

  const sources = Array.isArray(view?.sources) ? view.sources : [];
  const stale = view?.dataStatus?.state === 'stale';
  const confirmBody = (c) => {
    const lead = c.kind === 'add'
      ? `Link ${describe(c.record)}? ${c.record.camp
        ? `Its games show on ${athleteLabel}'s timeline. Camp and all-star games stay out of scoring.`
        : `Its games count as ${athleteLabel}'s, on the profile and in scoring.`}`
      : `Remove ${describe(c.record)}? Its games stop counting as ${athleteLabel}'s.`;
    const notes = c.kind === 'add' ? linkNotes(c.record, athleteLabel) : [];
    if (!notes.length) return lead;
    return (
      <span className="flex flex-col gap-2">
        <span>{lead}</span>
        {notes.map((note) => <span key={note} className="block">{note}</span>)}
      </span>
    );
  };

  return (
    <>
      <BmModal
        title="Linked Cerebro records"
        onClose={onClose}
        width={680}
        footer={(
          <>
            <Btn variant="secondary" onClick={refreshOnly} disabled={busy || loading || !view}>Refresh stats</Btn>
            <Btn onClick={onClose}>Done</Btn>
          </>
        )}
      >
        <p className="bm-body-s text-bm-muted">{athleteLabel}</p>
        <p className="mt-2 bm-body-m text-bm-ink">
          Cerebro sometimes files one player under more than one record. Link this athlete's other records and their
          games count as this athlete's, on the profile and in scoring. Camp and all-star games show on the timeline
          but stay out of scoring.
        </p>

        {loading && !view && <Loading label="Loading records..." />}
        {loadError && !view && (
          <div className="mt-5 flex flex-col items-start gap-3">
            <ErrorState role="alert">Could not load the linked records.</ErrorState>
            <Btn
              variant="secondary"
              size="sm"
              onClick={() => {
                load();
                if (suggestError) loadSuggestions();
              }}
            >
              Try again
            </Btn>
          </div>
        )}

        {view && (
          <div className="mt-5 flex flex-col gap-6">
            {view.cerebroError && (
              <p className="bm-body-s text-bm-muted">Cerebro did not answer, so only the record ids show.</p>
            )}

            {outcome && (
              <section className="flex flex-col gap-1" data-testid="refresh-outcome" role="status">
                <Overline>Last change</Overline>
                {outcome.map((item) => (
                  <p key={item.id} className="bm-body-s text-bm-ink" data-testid={`contribution-${item.id}`}>
                    {item.name}: {item.text}
                  </p>
                ))}
              </section>
            )}

            {(sources.length > 0 || stale) && (
              <section className="flex flex-col gap-1" data-testid="stats-sources">
                <Overline>Counted in the stats</Overline>
                {sources.map((s) => (
                  <p key={`${s.id}-${s.via}`} className="bm-body-s text-bm-muted">
                    {[s.displayName || s.team || s.id || 'Partner team', `${Number(s.scoringGames) || 0} ${Number(s.scoringGames) === 1 ? 'game' : 'games'} in the scoring season`].join(', ')}
                    {s.nameMismatch ? '. Its name no longer matches: check it.' : ''}
                  </p>
                ))}
                {stale && (
                  <p className="bm-body-s text-bm-critical">
                    The last refresh did not finish, so these are the stats saved before it.
                  </p>
                )}
              </section>
            )}

            <section className="flex flex-col gap-2">
              <Overline>Main record</Overline>
              {view.main ? (
                <ul><RecordLine testId="main-record" record={view.main} /></ul>
              ) : (
                <EmptyState>
                  No main Cerebro record. Link this athlete's records below: their games count as the athlete's, and
                  they stand in when the team's feed has no stats for the athlete.
                </EmptyState>
              )}
            </section>

            <section className="flex flex-col gap-2">
              <Overline>{`Linked records (${linked.length} of ${maxLinked})`}</Overline>
              {linked.length === 0 ? (
                <EmptyState>No linked records yet.</EmptyState>
              ) : (
                <ul className="flex flex-col gap-2">
                  {linked.map((record) => (
                    <RecordLine key={record.id} testId={`linked-${record.id}`} record={record}>
                      <Btn size="sm" variant="danger" onClick={() => setConfirm({ kind: 'remove', record })} disabled={busy}>
                        Remove
                      </Btn>
                    </RecordLine>
                  ))}
                </ul>
              )}
            </section>

            <section className="flex flex-col gap-2" data-testid="suggested-records">
              <Overline>Suggested records</Overline>
              <p className="bm-body-s text-bm-muted">
                Cerebro records that look like the same player. Check each one before you link it.
              </p>
              {suggesting && !suggestions && <p className="bm-body-s text-bm-muted">Looking for likely records...</p>}
              {suggestError && (
                <div className="flex flex-col items-start gap-3">
                  <ErrorState role="alert">{suggestError}</ErrorState>
                  <Btn variant="secondary" size="sm" onClick={loadSuggestions} disabled={suggesting}>Try again</Btn>
                </div>
              )}
              {suggestions && suggestions.length === 0 && <EmptyState>No likely records found.</EmptyState>}
              {suggestions && suggestions.length > 0 && (
                <ul className="flex flex-col gap-2">
                  {suggestions.map((s) => (
                    <RecordLine
                      key={s.id}
                      testId={`suggestion-${s.id}`}
                      record={s}
                      details={<SuggestionDetails suggestion={s} />}
                    >
                      {candidateAction(s)}
                    </RecordLine>
                  ))}
                </ul>
              )}
            </section>

            <section className="flex flex-col gap-2">
              <Overline>Find a record</Overline>
              <div className="flex flex-col gap-2 sm:flex-row">
                <SearchBox
                  aria-label="Player name or Cerebro player id"
                  value={name}
                  onChange={setName}
                  placeholder="Player name, or paste a Cerebro player id"
                  onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                  className="sm:flex-1"
                />
                <Btn onClick={runSearch} disabled={!canSearch || searching}>{searching ? 'Searching...' : 'Search'}</Btn>
              </div>
              {searchError && <ErrorState role="alert">{searchError}</ErrorState>}
              {results && results.length === 0 && (
                <EmptyState>{isRecordId(name) ? 'Cerebro has no player with that id.' : 'No Cerebro players match that name.'}</EmptyState>
              )}
              {results && results.length > 0 && (
                <ul className="flex max-h-80 flex-col gap-2 overflow-y-auto">
                  {results.map((c) => (
                    <RecordLine key={c.id} testId={`candidate-${c.id}`} record={c}>
                      {candidateAction(c)}
                    </RecordLine>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </BmModal>

      {confirm && (
        <ConfirmModal
          title={confirm.kind === 'add' ? 'Link this record?' : 'Remove this record?'}
          body={confirmBody(confirm)}
          requireAck={false}
          cancelLabel="Cancel"
          confirmLabel={confirm.kind === 'add' ? 'Yes, link it' : 'Yes, remove it'}
          tone={confirm.kind === 'add' ? 'primary' : 'danger'}
          busy={busy}
          onConfirm={() => submit(confirm)}
          onClose={() => { if (!busy) setConfirm(null); }}
        />
      )}
    </>
  );
};

export default LinkedCerebroRecordsDialog;
